import React, { useEffect, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import MuiContainer from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import toast from 'utils/toast';
import cookie from 'utils/cookie';
import { login } from 'store/modules/user/actions';
import { getVariable } from 'services/environmentVariable';
import { ConfigContext } from 'context/config';
import * as authService from 'services/auth.service';
import * as Styled from './styled';
import LoginFrom from './LoginForm';
import PromoEmbedSocial from './PromoEmbedSocial';
import PromoText from './PromoText';
import PromoVideo from './PromoVideo';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

const Login = ({ history, match, modal }) => {
  const [t] = useTranslation('auth');
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector((s) => s.user);
  const config = useContext(ConfigContext);
  const [loginType, setLoginType] = useState();
  const [showMfa, setShowMfa] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isLoggedIn && history) {
      history.push('/');
    } else {
      getVariable(`LOGIN_PAGE_TYPE_UI_${config.BRAND}`).then((resp) => {
        if (resp) {
          setLoginType(resp.value);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!match) return;
    const { token } = match.params;
    if (token) {
      loginWithToken(token);
    }
  }, []);

  const onLoginSuccess = (resp) => {
    dispatch(login(resp));
    if (history) {
      history.push('/');
    }
  };

  const loginWithToken = async (token) => {
    try {
      setLoading(true);
      const resp = await authService.loginWithToken(token);
      onLoginSuccess(resp);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
      history.push('/login');
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (values.verify_code) {
        const resp = await authService.verifyCode(
          showMfa.username,
          values.verify_code,
          showMfa.deviceId,
        );
        onLoginSuccess(resp);
      } else {
        const deviceId = cookie.get('device_id');
        const resp = await authService.login(
          values.email,
          values.password,
          deviceId,
        );
        if (values.remember) {
          cookie.set('username', values.email, {
            maxAge: 2147483647,
            path: '/',
          });
        } else {
          cookie.remove('username');
        }
        if (resp.mfa_pending) {
          setShowMfa({
            deviceId: resp.device_id,
            username: values.email,
            channel: resp.mfa_channel,
            message: resp.msg,
          });
        } else {
          onLoginSuccess(resp);
        }
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onResendCode = () =>
    authService.resendVerificationCode(showMfa.username, showMfa.deviceId);

  return (
    <Styled.Container modal={modal}>
      <Helmet title={t('auth:login', 'Login')} />
      <MuiContainer maxWidth="xl">
        <Grid container spacing={2}>
          <Grid item xs md={modal ? 12 : 5}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: modal ? 3 : 7.5,
              }}
            >
              {!modal && (
                <Box className="logo">
                  <a href="/">
                    <img src={`/assets/svg/tic-logo-full.svg`} alt="logo" />
                  </a>
                </Box>
              )}
              {!modal && (
                <Box textAlign="center" mb={3}>
                  <Typography gutterBottom variant="h1" fontSize={32}>
                    Save Time & Reach More People
                  </Typography>
                  <Typography color="primary" variant="h1" fontSize={32}>
                    Create Ministry Impact
                  </Typography>
                </Box>
              )}
              <div className="form-container">
                <LoginFrom
                  showMfa={showMfa}
                  modal={modal}
                  onSubmit={handleSubmit}
                  loading={loading}
                />
              </div>
            </Box>
          </Grid>
          {!modal && (
            <Grid item xs={false} md={7}>
              {loginType === 'text' && <PromoText />}
              {loginType === 'video' && <PromoVideo />}
              {loginType === 'reviews' && <PromoEmbedSocial />}
            </Grid>
          )}
        </Grid>
      </MuiContainer>
    </Styled.Container>
  );
};

Login.propTypes = {
  modal: PropTypes.bool,
};

export default Login;
